import {
  DEFAULT_CHUNK_LOG2,
  HEADER_SIZE,
  MAGIC,
  MAX_CHUNK_LOG2,
  MIN_CHUNK_LOG2,
  NONCE_PREFIX_SIZE,
  OFF_CHUNK_LOG2,
  OFF_FLAGS,
  OFF_MAGIC,
  OFF_NONCE_PREFIX,
  OFF_PLAINTEXT_LENGTH,
  OFF_RESERVED,
  OFF_SUITE,
  OFF_VERSION,
  RESERVED_SIZE,
  SUITE_AES256GCM,
  TAG_SIZE,
  VERSION,
} from "./constants.js";
import { randomBytes } from "./bytes.js";
import { LscError } from "./errors.js";

export interface Header {
  version: number;
  suite: number;
  chunkLog2: number;
  flags: number;
  /** L — plaintext length in bytes */
  plaintextLength: number;
  /** 7 random bytes, unique per file */
  noncePrefix: Uint8Array;
}

export interface BuildHeaderParams {
  plaintextLength: number;
  chunkLog2?: number;
  noncePrefix?: Uint8Array;
}

/** §5.1 — builds the 32-byte container header. */
export function buildHeader(p: BuildHeaderParams): Uint8Array {
  const chunkLog2 = p.chunkLog2 ?? DEFAULT_CHUNK_LOG2;
  if (!Number.isInteger(chunkLog2) || chunkLog2 < MIN_CHUNK_LOG2 || chunkLog2 > MAX_CHUNK_LOG2) {
    throw new LscError("BAD_INPUT", `chunkLog2 must be in ${MIN_CHUNK_LOG2}..${MAX_CHUNK_LOG2}`);
  }
  const L = p.plaintextLength;
  if (!Number.isSafeInteger(L) || L < 0) {
    throw new LscError("BAD_INPUT", "plaintextLength must be a non-negative safe integer");
  }
  const prefix = p.noncePrefix ?? randomBytes(NONCE_PREFIX_SIZE);
  if (prefix.length !== NONCE_PREFIX_SIZE) {
    throw new LscError("BAD_INPUT", `noncePrefix must be ${NONCE_PREFIX_SIZE} bytes`);
  }
  const h = new Uint8Array(HEADER_SIZE);
  h.set(MAGIC, OFF_MAGIC);
  h[OFF_VERSION] = VERSION;
  h[OFF_SUITE] = SUITE_AES256GCM;
  h[OFF_CHUNK_LOG2] = chunkLog2;
  h[OFF_FLAGS] = 0;
  new DataView(h.buffer).setBigUint64(OFF_PLAINTEXT_LENGTH, BigInt(L), false);
  h.set(prefix, OFF_NONCE_PREFIX);
  // reserved bytes stay zero
  return h;
}

/** §5.5 step 1 — strict header validation. Accepts exactly 32 bytes. */
export function parseHeader(bytes: Uint8Array): Header {
  if (bytes.length !== HEADER_SIZE) {
    throw new LscError("BAD_HEADER", `header must be ${HEADER_SIZE} bytes`);
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (bytes[OFF_MAGIC + i] !== MAGIC[i]) throw new LscError("BAD_MAGIC");
  }
  const version = bytes[OFF_VERSION]!;
  if (version !== VERSION) throw new LscError("UNSUPPORTED_VERSION", `version ${version}`);
  const suite = bytes[OFF_SUITE]!;
  if (suite !== SUITE_AES256GCM) throw new LscError("UNSUPPORTED_SUITE", `suite ${suite}`);
  const chunkLog2 = bytes[OFF_CHUNK_LOG2]!;
  if (chunkLog2 < MIN_CHUNK_LOG2 || chunkLog2 > MAX_CHUNK_LOG2) {
    throw new LscError("BAD_HEADER", `chunkLog2 ${chunkLog2} out of range`);
  }
  const flags = bytes[OFF_FLAGS]!;
  if (flags !== 0) throw new LscError("BAD_HEADER", "flags must be 0 in v1");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const big = view.getBigUint64(OFF_PLAINTEXT_LENGTH, false);
  if (big > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new LscError("BAD_HEADER", "plaintextLength exceeds 2^53-1");
  }
  for (let i = 0; i < RESERVED_SIZE; i++) {
    if (bytes[OFF_RESERVED + i] !== 0) throw new LscError("BAD_HEADER", "reserved bytes must be 0");
  }
  return {
    version,
    suite,
    chunkLog2,
    flags,
    plaintextLength: Number(big),
    noncePrefix: bytes.slice(OFF_NONCE_PREFIX, OFF_NONCE_PREFIX + NONCE_PREFIX_SIZE),
  };
}

export function chunkSizeOf(h: Pick<Header, "chunkLog2">): number {
  return 2 ** h.chunkLog2;
}

/** §5.2 — n = max(1, ceil(L / C)) */
export function chunkCount(plaintextLength: number, chunkSize: number): number {
  return Math.max(1, Math.ceil(plaintextLength / chunkSize));
}

/** §5.4 — E = 32 + L + 16·n */
export function encryptedSize(plaintextLength: number, chunkLog2: number = DEFAULT_CHUNK_LOG2): number {
  const C = 2 ** chunkLog2;
  return HEADER_SIZE + plaintextLength + TAG_SIZE * chunkCount(plaintextLength, C);
}

export function encryptedSizeOf(h: Header): number {
  return encryptedSize(h.plaintextLength, h.chunkLog2);
}

export function frameCount(h: Header): number {
  return chunkCount(h.plaintextLength, chunkSizeOf(h));
}

/** Plaintext length carried by frame i. */
export function framePlaintextLength(h: Header, index: number): number {
  const C = chunkSizeOf(h);
  const n = frameCount(h);
  if (!Number.isInteger(index) || index < 0 || index >= n) {
    throw new LscError("BAD_INPUT", `frame index ${index} out of range 0..${n - 1}`);
  }
  if (index < n - 1) return C;
  return h.plaintextLength - (n - 1) * C;
}

/** §5.4 — off(i) = 32 + i·(C + 16) */
export function frameOffset(h: Header, index: number): number {
  const C = chunkSizeOf(h);
  frameCount(h); // range check happens in framePlaintextLength
  framePlaintextLength(h, index);
  return HEADER_SIZE + index * (C + TAG_SIZE);
}

/** flen(i) = len_i + 16 */
export function frameLength(h: Header, index: number): number {
  return framePlaintextLength(h, index) + TAG_SIZE;
}

export interface FrameRange {
  firstFrame: number;
  lastFrame: number;
  /** byte offset of the first container byte to request */
  containerStart: number;
  /** exclusive end offset in the container */
  containerEnd: number;
  /** how many plaintext bytes to skip at the beginning of the first decrypted frame */
  skipInFirst: number;
}

/**
 * §5.4 — which frames (and which container byte range) cover plaintext [start, end).
 * Used by random access (media player, partial downloads).
 */
export function frameRangeForPlaintext(h: Header, start: number, end: number): FrameRange {
  const L = h.plaintextLength;
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end > L || start >= end) {
    throw new LscError("BAD_INPUT", `invalid plaintext range [${start}, ${end}) for L=${L}`);
  }
  const C = chunkSizeOf(h);
  const firstFrame = Math.floor(start / C);
  const lastFrame = Math.floor((end - 1) / C);
  return {
    firstFrame,
    lastFrame,
    containerStart: frameOffset(h, firstFrame),
    containerEnd: frameOffset(h, lastFrame) + frameLength(h, lastFrame),
    skipInFirst: start - firstFrame * C,
  };
}
